export type FlipkartCampaignEndDateJobStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type FlipkartCampaignEndDateTotals = {
  total?: number;
  applied?: number;
  failed?: number;
  skipped?: number;
  pending?: number;
};

export type FlipkartCampaignEndDateProgress = {
  processed?: number;
  total?: number;
  error?: string;
};

export type FlipkartCampaignEndDateJob = {
  id: string;
  status: FlipkartCampaignEndDateJobStatus | string;
  totals: FlipkartCampaignEndDateTotals;
  applyProgress: FlipkartCampaignEndDateProgress;
};

export type FlipkartCampaignEndDateResult = {
  id: string;
  campaignId: string;
  campaignName: string | null;
  status: string;
  errorMessage: string | null;
};

export const FLIPKART_END_DATE_TERMINAL_STATUSES = new Set([
  'completed',
  'completed_with_errors',
  'failed',
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function parseFlipkartCampaignEndDateJob(raw: unknown): FlipkartCampaignEndDateJob | null {
  const record = asRecord(raw);
  const id = asString(record?.id);
  const status = asString(record?.status);
  if (!record || !id || !status) return null;
  const totals = asRecord(record.totals) ?? {};
  const progress = asRecord(record.apply_progress ?? record.applyProgress) ?? {};
  return {
    id,
    status,
    totals: {
      total: asNumber(totals.total),
      applied: asNumber(totals.applied),
      failed: asNumber(totals.failed),
      skipped: asNumber(totals.skipped),
      pending: asNumber(totals.pending),
    },
    applyProgress: {
      processed: asNumber(progress.processed),
      total: asNumber(progress.total),
      error: asString(progress.error) ?? undefined,
    },
  };
}

export function parseFlipkartCampaignEndDateResult(
  raw: unknown
): FlipkartCampaignEndDateResult | null {
  const record = asRecord(raw);
  const id = asString(record?.id);
  const campaignId = asString(record?.campaign_id ?? record?.campaignId);
  if (!record || !id || !campaignId) return null;
  return {
    id,
    campaignId,
    campaignName: asString(record.campaign_name ?? record.campaignName),
    status: asString(record.status) ?? 'failed',
    errorMessage: asString(record.error_message ?? record.errorMessage),
  };
}
